// Permission keys — plan §2.2 yetki matrisi
// roles.permissions JSONB'sinde bu shape ile saklanır. Admin custom rol
// tanımlayabildiği için type literal union + index signature (extensible).

export const PERMISSION_KEYS = [
  // System
  "system.admin",

  // Users
  "users.invite",
  "users.change_role",
  "users.deactivate",

  // Clients
  "clients.create",
  "clients.list_all",
  "clients.edit_own",
  "clients.edit_all",
  "clients.delete",
  "clients.restore",

  // Projects
  "projects.create",
  "projects.assign_users",
  "projects.view_all",
  "projects.view_assigned",
  "projects.edit_responses",
  "projects.trigger_generation",
  "projects.regenerate_section",
  "projects.approve_section",
  "projects.download_docx",
  "projects.reopen",
  "projects.invite_client", // Müşteri katılımcı davet et (plan: 2026-07-23-musteri-katilimci-rolu)
  "projects.import_report", // Geçmiş rapor aktarımı (plan: 2026-09-24-report-import)

  // Framework
  "framework.create",
  "framework.edit",
  "framework.publish",
  "framework.delete_published", // M13: orphan published version sil/edit

  // Sector packages (M13)
  "sector_packages.create",
  "sector_packages.edit",
  "sector_packages.publish",
  "sector_packages.delete",

  // Settings
  "settings.llm",
  "settings.smtp",
  "settings.system_prompt",
  "settings.audit_log",
  "settings.cost_dashboard",
  "settings.branding",

  // QC (Kalite Kontrol) Prompt Library
  "qc_prompts.manage",

  // Performans Göstergeleri (M16 KPI — plan: 2026-09-08-kpi-module; sync migration 0044)
  "kpi.view", // Veri Girişi / Özet / Katalog okuma + Excel export
  "kpi.edit", // değer girişi, içe aktarma, kanıt bağlama
  "kpi.manage", // set/katalog/işletme yönetimi, müşteri erişimi, müşteri yöneticisi daveti
  "kpi.verify", // müşteri girdisini onaylama / açıklama isteme (sert kapı K11)
  // Finar rev. madde 12 (2026-10-30, sync migration 0067): global Veri Kataloğu'nda KATEGORİ
  // düğümü oluştur/taşı/yeniden adlandır/kaldır (+ kategori satırlı katalog içe aktarımı).
  // Yaprak gösterge işlemleri `kpi.manage`'de kalır. Varsayılan yalnız `superadmin` rolünde.
  "kpi.catalog.categories",

  // Archive
  "archive.view_all",
  "archive.view_assigned",
  "archive.download_old_docx",
  "archive.export_csv",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/**
 * Türkçe okunabilir etiketler — RolesList UI'da gösterilir, altında küçük
 * sistem ismi (key) silik gözükür. Yeni permission eklenirken bu mapping de
 * güncellenmeli; eksik key için fallback `key` kendisidir.
 */
export const PERMISSION_LABELS_TR: Record<PermissionKey, string> = {
  "system.admin": "Sistem Yöneticisi",
  "users.invite": "Kullanıcı Davet Et",
  "users.change_role": "Kullanıcı Rolünü Değiştir",
  "users.deactivate": "Kullanıcı Deaktif Et",
  "clients.create": "Müşteri Oluştur",
  "clients.list_all": "Tüm Müşterileri Listele",
  "clients.edit_own": "Kendi Müşterilerini Düzenle",
  "clients.edit_all": "Tüm Müşterileri Düzenle",
  "clients.delete": "Müşteri Sil",
  "clients.restore": "Müşteri Geri Yükle",
  "projects.create": "Proje Oluştur",
  "projects.assign_users": "Projeye Kullanıcı Ata",
  "projects.view_all": "Tüm Projeleri Görüntüle",
  "projects.view_assigned": "Atanmış Projeleri Görüntüle",
  "projects.edit_responses": "Proje Yanıtlarını Düzenle",
  "projects.trigger_generation": "AI Üretimini Başlat",
  "projects.regenerate_section": "Bölümü Yeniden Üret",
  "projects.approve_section": "Bölümü Onayla",
  "projects.download_docx": "DOCX İndir",
  "projects.reopen": "Projeyi Yeniden Aç",
  "projects.invite_client": "Müşteri Katılımcı Davet Et",
  "projects.import_report": "Geçmiş rapor aktarımı",
  "kpi.view": "Performans Göstergelerini Görüntüle",
  "kpi.edit": "Performans Göstergesi Değeri Gir",
  "kpi.manage": "Performans Göstergesi Veri Setlerini Yönet",
  "kpi.verify": "Müşteri Girdisini Onayla",
  "kpi.catalog.categories": "Veri Kataloğu Kategorilerini Yönet",
  "framework.create": "Çerçeve Oluştur",
  "framework.edit": "Çerçeve Düzenle",
  "framework.publish": "Çerçeve Yayınla",
  "framework.delete_published": "Yayında Çerçeve Sürümünü Sil",
  "sector_packages.create": "Sektör Paketi Oluştur",
  "sector_packages.edit": "Sektör Paketi Düzenle",
  "sector_packages.publish": "Sektör Paketi Yayınla",
  "sector_packages.delete": "Sektör Paketi Sil",
  "settings.llm": "LLM Ayarları",
  "settings.smtp": "SMTP Ayarları",
  "settings.system_prompt": "Sistem Prompt Ayarları",
  "settings.audit_log": "Audit Log Görüntüle",
  "settings.cost_dashboard": "Maliyet Dashboard",
  "settings.branding": "Branding Ayarları",
  "qc_prompts.manage": "KK Prompt Library Yönetimi",
  "archive.view_all": "Tüm Arşivi Görüntüle",
  "archive.view_assigned": "Atanmış Arşivi Görüntüle",
  "archive.download_old_docx": "Eski DOCX İndir",
  "archive.export_csv": "Arşiv CSV Dışa Aktar",
};

/**
 * Helper: key → Türkçe etiket. Bilinmeyen key için key'in kendisi döner
 * (custom rol oluşturulduğunda admin custom key tanımlayabilir).
 */
export function getPermissionLabel(key: string): string {
  return (PERMISSION_LABELS_TR as Record<string, string>)[key] ?? key;
}

/**
 * `roles.permissions` JSONB shape. Known key'ler tipli; custom key'lere
 * izin vermek için index signature (admin custom rol tanımlarsa).
 */
export type Permissions = Partial<Record<PermissionKey, boolean>> & Record<string, boolean>;

/**
 * Permission grupları (RolesList UI'da yetki matrisi başlıkları).
 * Single source of truth — yeni grup eklerken sadece bu listeyi güncelle,
 * frontend `PERMISSION_GROUPS`'tan tüketir, hardcoded liste yok.
 *
 * Sıra önemli: UI'da bu sırayla render edilir. Yeni grup eklerken:
 *   1. PERMISSION_KEYS array'ine yeni key(ler) ekle
 *   2. PERMISSION_LABELS_TR'ye TR etiket ekle
 *   3. PERMISSION_GROUPS'a `{ label, prefix }` ekle
 *   4. DEFAULT_ROLE_PERMISSIONS.admin'de (ve superadmin'de) true set et
 *   5. Yeni bir Drizzle migration ile mevcut DB rollerine sync et — `WHERE name IN
 *      ('superadmin', 'admin', …)`: superadmin 0067'de admin'in tek seferlik kopyası, sonraki
 *      anahtarlar ona OTOMATİK geçmez (kural: [feedback_permission_sync_migration]; kilit
 *      tests/integration/superadmin-role.test.ts "superadmin ⊇ admin")
 */
export const PERMISSION_GROUPS = [
  { label: "Sistem", prefix: "system." },
  { label: "Kullanıcılar", prefix: "users." },
  { label: "Müşteriler", prefix: "clients." },
  { label: "Projeler", prefix: "projects." },
  { label: "Çerçeveler", prefix: "framework." },
  { label: "Sektör Paketleri", prefix: "sector_packages." },
  { label: "KK Prompt Library", prefix: "qc_prompts." },
  { label: "Performans Göstergeleri", prefix: "kpi." },
  { label: "Ayarlar", prefix: "settings." },
  { label: "Arşiv", prefix: "archive." },
] as const satisfies readonly { label: string; prefix: string }[];

export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

/**
 * Sistem rolleri için default permission matrisi — plan §2.2'den türetilmiş.
 * Migration seed'inde bu değerler yazılır. Admin UI'dan custom rol
 * tanımlarsa bu matrise bakmaz.
 *
 * `superadmin` (Sistem Yöneticisi — Finar rev. madde 12, migration 0067) = admin'in tamamı +
 * `kpi.catalog.categories`. Admin bu anahtarı TAŞIMAZ.
 */
const ADMIN_PERMISSIONS: Permissions = {
  "system.admin": true,
  "users.invite": true,
  "users.change_role": true,
  "users.deactivate": true,
  "clients.create": true,
  "clients.list_all": true,
  "clients.edit_own": true,
  "clients.edit_all": true,
  "clients.delete": true,
  "clients.restore": true,
  "projects.create": true,
  "projects.assign_users": true,
  "projects.view_all": true,
  "projects.view_assigned": true,
  "projects.edit_responses": true,
  "projects.trigger_generation": true,
  "projects.regenerate_section": true,
  "projects.approve_section": true,
  "projects.download_docx": true,
  "projects.reopen": true,
  "projects.invite_client": true,
  "projects.import_report": true,
  "kpi.view": true,
  "kpi.edit": true,
  "kpi.manage": true,
  "kpi.verify": true,
  "framework.create": true,
  "framework.edit": true,
  "framework.publish": true,
  "framework.delete_published": true,
  "sector_packages.create": true,
  "sector_packages.edit": true,
  "sector_packages.publish": true,
  "sector_packages.delete": true,
  "settings.llm": true,
  "settings.smtp": true,
  "settings.system_prompt": true,
  "settings.audit_log": true,
  "settings.cost_dashboard": true,
  "settings.branding": true,
  "qc_prompts.manage": true,
  "archive.view_all": true,
  "archive.view_assigned": true,
  "archive.download_old_docx": true,
  "archive.export_csv": true,
};

export const DEFAULT_ROLE_PERMISSIONS: Record<
  "superadmin" | "admin" | "consultant" | "writer" | "musteri",
  Permissions
> = {
  superadmin: { ...ADMIN_PERMISSIONS, "kpi.catalog.categories": true },
  admin: ADMIN_PERMISSIONS,
  consultant: {
    "clients.create": true,
    "clients.list_all": true,
    "clients.edit_own": true,
    "projects.create": true,
    "projects.assign_users": true, // sadece kendi projesine
    "projects.view_assigned": true,
    "projects.edit_responses": true,
    "projects.trigger_generation": true,
    "projects.regenerate_section": true,
    "projects.approve_section": true,
    "projects.download_docx": true,
    "projects.invite_client": true,
    "projects.import_report": true,
    "kpi.view": true,
    "kpi.edit": true,
    "kpi.manage": true,
    "kpi.verify": true,
    "archive.view_assigned": true,
    "archive.download_old_docx": true,
  },
  writer: {
    "projects.view_assigned": true,
    "projects.edit_responses": true,
    "projects.download_docx": true,
    "archive.view_assigned": true,
    "archive.download_old_docx": true,
  },
  // Müşteri katılımcı — sert sınır rol izinleri değil, `users.user_kind='client'` +
  // global whitelist middleware ile sağlanır (client-gate, Task 3). Bu rolün
  // permissions seti bilinçli olarak boş.
  musteri: {},
};
